import React from 'react';
import {
  Search, Calendar, ArrowRight, Loader2, CheckCircle2,
  X, Cpu, BarChart3, ChevronDown, ChevronRight as ChevronRightIcon,
} from 'lucide-react';
import { useEventsTasksTab } from '../../hooks/useEventsTasksTab';
import { EVENT_TYPES, SEVERITIES, EVENT_STATUSES, TASK_STATUSES } from '../../constants/eventStyles';
import type { EventType, SeverityLevel, EventStatus } from '../../redux/api/eventsApi';
import type { TaskStatus } from '../../redux/api/tasksApi';
import EventCard from '../events/EventCard';
import EventsTimelineStrip from '../events/EventsTimelineStrip';
import EventDetailPanel from '../events/EventDetailPanel';
import EventsStatsPanel from '../events/EventsStatsPanel';
import TasksStatsPanel from '../events/TasksStatsPanel';
import TaskCard from './TaskCard';
import { useComponentPowerMap, getTotalStcPowerKw } from '../../hooks/useComponentPowerMap';

const EventsTasksTab: React.FC = () => {
  const {
    siteId, viewMode, setViewMode, search, setSearch,
    componentFilter, setComponentFilter,
    taskStatusFilter, setTaskStatusFilter,
    taskSortBy, setTaskSortBy, taskSortDir, setTaskSortDir,
    eventType, setEventType, severity, setSeverity,
    eventStatus, setEventStatus, themeId, setThemeId,
    selectedEventId, setSelectedEventId,
    expandedTaskId, setExpandedTaskId,
    events, tasks, themes,
    filteredTasks, filteredEvents,
    taskComponentMap, eventComponentMap, taskEventDateMap,
    allComponents, handleComponentClick, handleEventClick,
    handleOpenTimeline, selectClass, isLoading, eventsError,
    activeItemCount, totalItemCount,
    showDashboard, setShowDashboard,
    eventStats, taskStats, taskStatsLoading,
  } = useEventsTasksTab();

  const { powerMap } = useComponentPowerMap();

  if (!siteId) {
    return (
      <div className="flex items-center justify-center h-full text-[var(--color-text-muted)]">
        Select a site to view events
      </div>
    );
  }

  return (
    <div className="eg-page-shell relative overflow-hidden">
      <div className="eg-toolbar-header shrink-0 px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-md border border-[var(--color-input-border)] overflow-hidden mr-2">
            <button
              onClick={() => setViewMode('tasks')}
              className={`px-3 h-8 text-xs font-medium transition-colors ${
                viewMode === 'tasks'
                  ? 'bg-[#17c480] text-white'
                  : 'bg-[var(--color-input-bg)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)]'
              }`}
            >
              Tasks {tasks.length > 0 && `(${componentFilter && filteredTasks.length !== tasks.length ? `${filteredTasks.length}/` : ''}${tasks.length})`}
            </button>
            <button
              onClick={() => setViewMode('events')}
              className={`px-3 h-8 text-xs font-medium transition-colors ${
                viewMode === 'events'
                  ? 'bg-[#17c480] text-white'
                  : 'bg-[var(--color-input-bg)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)]'
              }`}
            >
              Events {events.length > 0 && `(${componentFilter && filteredEvents.length !== events.length ? `${filteredEvents.length}/` : ''}${events.length})`}
            </button>
          </div>

          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--color-text-muted)]" />
            <input
              type="text"
              placeholder={viewMode === 'tasks' ? 'Search tasks...' : 'Search events...'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`${selectClass} w-full pl-8`}
            />
          </div>

          {viewMode === 'tasks' && (
            <>
              <select value={taskSortBy} onChange={(e) => setTaskSortBy(e.target.value)} className={selectClass}>
                <option value="event_date">Event Date</option>
                <option value="priority">Priority</option>
                <option value="due_date">Due Date</option>
                <option value="created_at">Created</option>
              </select>
              <button
                onClick={() => setTaskSortDir((d) => d === 'desc' ? 'asc' : 'desc')}
                className={`${selectClass} cursor-pointer`}
              >
                {taskSortDir === 'desc' ? 'Newest' : 'Oldest'}
              </button>
              <select value={taskStatusFilter} onChange={(e) => setTaskStatusFilter(e.target.value as TaskStatus | '')} className={selectClass}>
                {TASK_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </>
          )}

          {viewMode === 'events' && (
            <>
              <select value={eventType} onChange={(e) => setEventType(e.target.value as EventType | '')} className={selectClass}>
                {EVENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <select value={severity} onChange={(e) => setSeverity(e.target.value as SeverityLevel | '')} className={selectClass}>
                {SEVERITIES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <select value={eventStatus} onChange={(e) => setEventStatus(e.target.value as EventStatus | '')} className={selectClass}>
                {EVENT_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              {themes.length > 0 && (
                <select value={themeId} onChange={(e) => setThemeId(e.target.value)} className={selectClass}>
                  <option value="">All Themes</option>
                  {themes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              )}
            </>
          )}

          {allComponents.size > 0 && (
            <select
              value={componentFilter}
              onChange={(e) => setComponentFilter(e.target.value)}
              className={selectClass}
            >
              <option value="">All Components</option>
              {Array.from(allComponents.entries())
                .sort((a, b) => b[1].count - a[1].count)
                .map(([ident, info]) => (
                  <option key={ident} value={ident}>
                    {ident} ({info.count})
                  </option>
                ))}
            </select>
          )}

          <button
            onClick={handleOpenTimeline}
            className="ml-auto flex items-center gap-1.5 h-8 px-3 text-xs font-medium text-[#17c480] border border-[#17c480]/30 rounded-md hover:bg-[#17c480]/10 transition-colors"
          >
            <Calendar className="w-3.5 h-3.5" />
            Timeline
            <ArrowRight className="w-3 h-3" />
          </button>
        </div>

        {componentFilter && (
          <div className="flex items-center gap-2 mt-2">
            <span className="text-[10px] text-[var(--color-text-muted)] uppercase tracking-wide">Filtering:</span>
            <button
              onClick={() => setComponentFilter('')}
              className="flex items-center gap-1.5 pl-2 pr-1.5 py-1 text-xs font-medium rounded-lg bg-[#17c480]/10 text-[#17c480] border border-[#17c480]/25 hover:bg-[#17c480]/15 transition-colors"
            >
              <Cpu className="w-3.5 h-3.5" />
              {componentFilter}
              <span className="text-[10px] text-[#17c480]/60 mx-0.5">
                ({activeItemCount}/{totalItemCount})
              </span>
              <X className="w-3.5 h-3.5 ml-0.5 opacity-60 hover:opacity-100" />
            </button>
          </div>
        )}
      </div>

      {(filteredEvents.length > 0 || filteredTasks.length > 0) && (
        <div className="eg-toolbar-header shrink-0 px-4 py-1">
          <EventsTimelineStrip
            events={viewMode === 'events' ? filteredEvents : events}
            tasks={viewMode === 'tasks' ? filteredTasks : tasks}
            dataMode={viewMode}
            taskEventDateMap={taskEventDateMap}
            onEventClick={handleEventClick}
            height={56}
          />
        </div>
      )}

      {(events.length > 0 || tasks.length > 0) && (
        <div className="shrink-0" style={{ borderBottom: '1px solid var(--color-overlay-light)' }}>
          <button
            onClick={() => setShowDashboard((prev) => !prev)}
            className="w-full flex items-center gap-2 px-4 py-2 text-xs font-medium text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-hover)] transition-colors"
          >
            <BarChart3 className="w-3.5 h-3.5" />
            <span>Statistics</span>
            {showDashboard
              ? <ChevronDown className="w-3.5 h-3.5 ml-auto" />
              : <ChevronRightIcon className="w-3.5 h-3.5 ml-auto" />
            }
          </button>
          {showDashboard && (
            <div className="px-4 pb-3">
              {viewMode === 'events' ? (
                <EventsStatsPanel eventStats={eventStats} />
              ) : (
                <TasksStatsPanel taskStats={taskStats} tasks={tasks} isLoading={taskStatsLoading} />
              )}
            </div>
          )}
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-4 py-3" style={{ background: 'var(--color-zone-recessed)' }}>
        {isLoading && (
          <div className="flex items-center justify-center py-16 gap-2 text-[var(--color-text-muted)]">
            <Loader2 className="w-5 h-5 animate-spin" />
            Loading {viewMode}...
          </div>
        )}

        {eventsError && viewMode === 'events' && (
          <div className="text-sm text-red-500 rounded-lg p-4" style={{ backgroundColor: 'rgba(239, 68, 68, 0.08)' }}>
            Failed to load data. Please try again.
          </div>
        )}

        {!isLoading && viewMode === 'tasks' && (
          filteredTasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-[var(--color-text-muted)]">
              <CheckCircle2 className="w-10 h-10 mb-3 opacity-40" />
              <p className="text-sm font-medium">
                {componentFilter ? 'No tasks match this component' : 'No tasks found'}
              </p>
              <p className="text-xs mt-1">
                {componentFilter
                  ? <button onClick={() => setComponentFilter('')} className="text-[#17c480] hover:underline">Clear filter</button>
                  : 'Tasks are auto-created when event logs are processed'
                }
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredTasks.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  siteId={siteId}
                  isExpanded={expandedTaskId === task.id}
                  onToggle={() => setExpandedTaskId(expandedTaskId === task.id ? null : task.id)}
                  onEventClick={handleEventClick}
                  onComponentClick={handleComponentClick}
                  components={taskComponentMap.get(task.id)}
                  activeComponent={componentFilter}
                  eventDate={taskEventDateMap.get(task.id)}
                  connectedPowerKw={getTotalStcPowerKw(taskComponentMap.get(task.id) || [], powerMap)}
                />
              ))}
            </div>
          )
        )}

        {!isLoading && !eventsError && viewMode === 'events' && (
          filteredEvents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-[var(--color-text-muted)]">
              <Calendar className="w-10 h-10 mb-3 opacity-40" />
              <p className="text-sm font-medium">
                {componentFilter ? 'No events match this component' : 'No events found'}
              </p>
              <p className="text-xs mt-1">
                {componentFilter
                  ? <button onClick={() => setComponentFilter('')} className="text-[#17c480] hover:underline">Clear filter</button>
                  : 'Upload an event log to extract events automatically'
                }
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredEvents.map((event) => (
                <EventCard
                  key={event.id}
                  event={event}
                  onClick={handleEventClick}
                  components={eventComponentMap.get(event.id)}
                  activeComponent={componentFilter}
                  onComponentClick={handleComponentClick}
                  connectedPowerKw={getTotalStcPowerKw(eventComponentMap.get(event.id) || [], powerMap)}
                />
              ))}
            </div>
          )
        )}
      </div>

      {selectedEventId && (
        <div className="absolute top-0 right-0 h-full z-10">
          <EventDetailPanel
            siteId={siteId}
            eventId={selectedEventId}
            onClose={() => setSelectedEventId(null)}
          />
        </div>
      )}
    </div>
  );
};

export default EventsTasksTab;
